import { NavSectionBlock } from "./components/NavSectionBlock";
import { SidebarToggle } from "./components/SidebarToggle";
import { NavAccordionProvider } from "./components/NavLink";
import { SidebarConfigProvider, useSidebarConfig } from "./SidebarConfigContext";
import { SafecheckLogo } from "@/modules/safecheck/components/SafecheckLogo";
import { HelpCenter } from "@/modules/safecheck/components/HelpCenter";
import { useSidebar } from "@/modules/dashboard-shell/SidebarProvider";
import { cn } from "@/shared/lib/cn";
import { X } from "lucide-react";

function MainSidebarNav() {
  const config = useSidebarConfig();
  const { isOpen } = useSidebar();

  return (
    <div className={isOpen ? "space-y-5" : "space-y-3"}>
      {config.sections.map((section) => (
        <NavSectionBlock key={section.id} section={section} />
      ))}
    </div>
  );
}

/** Vagues lumineuses vertes en bas du menu. */
function SidebarWaves() {
  return (
    <svg
      className="pointer-events-none absolute inset-x-0 bottom-0 -z-10 h-72 w-full"
      viewBox="0 0 280 288"
      preserveAspectRatio="none"
      fill="none"
      aria-hidden="true"
    >
      <defs>
        <linearGradient id="sidebar-wave" x1="0" y1="1" x2="1" y2="0">
          <stop offset="0" stopColor="#a6f21b" stopOpacity="0.55" />
          <stop offset="0.6" stopColor="#3fbf5a" stopOpacity="0.18" />
          <stop offset="1" stopColor="#3fbf5a" stopOpacity="0" />
        </linearGradient>
        <linearGradient id="sidebar-wave-fill" x1="0" y1="1" x2="0.8" y2="0">
          <stop offset="0" stopColor="#3fbf5a" stopOpacity="0.32" />
          <stop offset="1" stopColor="#3fbf5a" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d="M0 288V170C60 196 118 236 280 168V288Z" fill="url(#sidebar-wave-fill)" />
      <path d="M0 150C70 190 140 230 280 150" stroke="url(#sidebar-wave)" strokeWidth="1.5" />
      <path d="M0 182C80 214 150 250 280 186" stroke="url(#sidebar-wave)" strokeWidth="1.2" />
      <path d="M0 214C70 240 160 268 280 220" stroke="url(#sidebar-wave)" strokeWidth="1" />
      <path d="M0 246C90 266 170 284 280 254" stroke="url(#sidebar-wave)" strokeWidth="0.8" />
    </svg>
  );
}

export function MainSidebar() {
  const { isOpen, close } = useSidebar();

  return (
    <aside
      className={cn(
        // Panneau vert sombre pleine hauteur, collé au bord gauche.
        "main-sidebar fixed inset-y-0 left-0 z-50 flex h-[100dvh] w-[var(--sidebar-width)] flex-col overflow-hidden bg-sidebar-bg transition-all duration-300 ease-in-out",
        "lg:sticky lg:top-0 lg:z-40 lg:h-screen lg:self-start",
        // Replié sur desktop : rail étroit qui garde les icônes visibles. Sur mobile, le menu sort de l'écran.
        "shadow-[var(--shadow)] lg:shadow-none",
        isOpen
          ? "translate-x-0"
          : "-translate-x-full lg:w-[var(--sidebar-collapsed-width)] lg:translate-x-0",
      )}
    >
      <SidebarWaves />
      <SidebarConfigProvider>
        <div
          className={cn(
            "sidebar-inner relative flex h-full min-h-0 w-[var(--sidebar-width)] shrink-0 flex-col py-6",
            isOpen ? "px-4" : "px-4 lg:w-[var(--sidebar-collapsed-width)] lg:px-3",
          )}
        >
          <header className={cn("sidebar-header sticky top-0 z-10 shrink-0", isOpen ? "-mx-4 px-4" : "-mx-4 px-4 lg:-mx-3 lg:px-3")}>
            <div className={cn("mb-8 flex items-center gap-2", isOpen ? "justify-between px-2" : "flex-col justify-center")}>
              <SafecheckLogo size="md" variant="onDark" markOnly={!isOpen} />
              {/* Réduire / agrandir le menu (desktop) ; sur mobile, la croix ci-dessous ferme le panneau. */}
              <SidebarToggle className="hidden lg:grid" />
              <button
                type="button"
                onClick={close}
                aria-label="Fermer le menu"
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-sidebar-muted transition-colors hover:bg-sidebar-hover hover:text-sidebar-fg lg:hidden"
              >
                <X size={20} />
              </button>
            </div>
          </header>
          <nav
            aria-label="Navigation principale"
            className="sidebar-nav sidebar-nav-scroll min-h-0 flex-1 overflow-y-auto overflow-x-hidden py-1"
          >
            <NavAccordionProvider>
              <MainSidebarNav />
            </NavAccordionProvider>
          </nav>
          {/* Aide : FAQ, manuel d'utilisation, plainte ou requête aux Admin. */}
          <HelpCenter expanded={isOpen} />
        </div>
      </SidebarConfigProvider>
    </aside>
  );
}
